import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { AppShell3 } from '@/components/app-shell-3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash,
  Loader2,
  Check,
  X,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

// Animation variants
const rowVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 }
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2,
      ease: 'easeIn'
    }
  }
};

const cellVariants = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  }
};

export function PPEManagementPage() {
  const { projectId } = useParams();
  const { user, userProfile, company } = useAuth();

  // Set page title
  usePageTitle('PPE Management');

  const [ppeRecords, setPpeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [project, setProject] = useState(null);
  const [newRecord, setNewRecord] = useState({
    source: '',
    type: '',
    quantity: '',
    cost: '',
    category: '',
    employee_id: '',
    date_issued: '',
    date_returned: ''
  });
  const [editingData, setEditingData] = useState({});
  const sourceInputRef = useRef(null);

  // Fetch project details
  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  // Fetch PPE records on mount and set up real-time subscription
  useEffect(() => {
    if (projectId && company?.id) {
      fetchPPERecords();

      // Set up real-time subscription
      const subscription = supabase
        .channel(`ppe-${projectId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'ppe_management',
            filter: `project_id=eq.${projectId}`
          },
          payload => {
            console.log('PPE record change:', payload);
            fetchPPERecords();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [projectId, company?.id]);

  // Focus on source input when adding new record
  useEffect(() => {
    if (isAddingNew && sourceInputRef.current) {
      sourceInputRef.current.focus();
    }
  }, [isAddingNew]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      setProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchPPERecords = async () => {
    try {
      setLoading(true);
      const { data, error} = await supabase
        .from('ppe_management')
        .select(`
          *,
          user_profiles!recorded_by (
            id,
            first_name,
            last_name
          ),
          employee:user_profiles!employee_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (error) throw error;
      setPpeRecords(data || []);
    } catch (error) {
      console.error('Error fetching PPE records:', error);
      toast.error('Failed to load PPE records');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewRecord({
      source: '',
      type: '',
      quantity: '',
      cost: '',
      category: '',
      employee_id: '',
      date_issued: '',
      date_returned: ''
    });
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewRecord({
      source: '',
      type: '',
      quantity: '',
      cost: '',
      category: '',
      employee_id: '',
      date_issued: '',
      date_returned: ''
    });
  };

  const handleSaveNew = async () => {
    try {
      const { error } = await supabase.from('ppe_management').insert([
        {
          project_id: projectId,
          company_id: company.id,
          source: newRecord.source,
          type: newRecord.type,
          quantity: newRecord.quantity,
          cost: newRecord.cost,
          category: newRecord.category,
          employee_id: newRecord.employee_id || null,
          date_issued: newRecord.date_issued || null,
          date_returned: newRecord.date_returned || null,
          recorded_by: userProfile.id
        }
      ]);

      if (error) throw error;

      toast.success('PPE record added successfully');
      setIsAddingNew(false);
      setNewRecord({
        source: '',
        type: '',
        quantity: '',
        cost: '',
        category: '',
        employee_id: '',
        date_issued: '',
        date_returned: ''
      });
      fetchPPERecords();
    } catch (error) {
      console.error('Error adding PPE record:', error);
      toast.error('Failed to add PPE record');
    }
  };

  const handleEdit = record => {
    setEditingRecordId(record.id);
    setEditingData({
      source: record.source || '',
      type: record.type || '',
      quantity: record.quantity || '',
      cost: record.cost || '',
      category: record.category || '',
      employee_id: record.employee_id || '',
      date_issued: record.date_issued ? record.date_issued.split('T')[0] : '',
      date_returned: record.date_returned ? record.date_returned.split('T')[0] : ''
    });
  };

  const handleCancelEdit = () => {
    setEditingRecordId(null);
    setEditingData({});
  };

  const handleSaveEdit = async recordId => {
    try {
      const { error } = await supabase
        .from('ppe_management')
        .update({
          source: editingData.source,
          type: editingData.type,
          quantity: editingData.quantity,
          cost: editingData.cost,
          category: editingData.category,
          employee_id: editingData.employee_id || null,
          date_issued: editingData.date_issued || null,
          date_returned: editingData.date_returned || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', recordId);

      if (error) throw error;

      toast.success('PPE record updated successfully');
      setEditingRecordId(null);
      setEditingData({});
      fetchPPERecords();
    } catch (error) {
      console.error('Error updating PPE record:', error);
      toast.error('Failed to update PPE record');
    }
  };

  const handleDelete = async recordId => {
    if (!confirm('Are you sure you want to delete this PPE record?')) return;

    try {
      const { error } = await supabase
        .from('ppe_management')
        .delete()
        .eq('id', recordId);

      if (error) throw error;

      toast.success('PPE record deleted successfully');
      fetchPPERecords();
    } catch (error) {
      console.error('Error deleting PPE record:', error);
      toast.error('Failed to delete PPE record');
    }
  };

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Search for employees (team members in current space or company members)
  const searchEmployees = async (query) => {
    try {
      // Use the same RPC function that the Team tool uses
      const { data: teamMembers, error: teamError } = await supabase.rpc(
        'get_team_members_with_stats',
        { p_project_id: projectId }
      );

      if (teamError) {
        console.error('Error fetching team members:', teamError);
        throw teamError;
      }

      console.log('Team members found via RPC:', teamMembers);

      // Filter and format results
      const allResults = (teamMembers || [])
        .filter(tm => tm.user_id) // Only include members with user_id
        .map(tm => ({
          value: tm.user_id,
          label: tm.name,
          data: tm
        }));

      console.log('Formatted results:', allResults);

      // If no query, return all results
      if (!query || query.trim() === '' || query === ' ') {
        console.log('Returning all results:', allResults);
        return allResults;
      }

      // Otherwise filter by query
      const filtered = allResults.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.data.email?.toLowerCase().includes(query.toLowerCase())
      );

      console.log('Filtered results:', filtered);
      return filtered;
    } catch (error) {
      console.error('Error searching employees:', error);
      return [];
    }
  };

  return (
    <AppShell3
      breadcrumbs={[
        { label: project?.name || 'Space', href: `/space/${projectId}` },
        { label: 'PPE Management' }
      ]}
      pageTitle="PPE Management"
      pageDescription="Manage PPE equipment tracking and records"
    >
      <div className="container mx-auto px-4 py-6 max-w-[1400px]">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              PPE Records
            </h2>
            <Button
              onClick={handleAddNew}
              disabled={isAddingNew}
              size="sm"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Record
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full caption-bottom text-sm table-fixed">
              <thead className="[&_tr]:border-b">
                <tr className="border-b">
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[9%]">
                    Source
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[9%]">
                    Type
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[6%]">
                    Quantity
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[7%]">
                    Cost
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[9%]">
                    Category
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[10%]">
                    Employee
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[11%]">
                    Date Issued
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[11%]">
                    Date Returned
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[10%]">
                    Uploaded Date
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[10%]">
                    Recorded By
                  </th>
                  <th className="text-foreground h-10 px-3 text-left align-middle font-medium w-[8%]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {loading ? (
                  <tr className="border-b">
                    <td colSpan={10} className="p-2 text-center py-8">
                      <motion.div
                        className="flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </motion.div>
                    </td>
                  </tr>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {/* New record row */}
                    {isAddingNew && (
                      <motion.tr
                        key="new-record"
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="border-b bg-blue-50"
                      >
                        <td className="p-2">
                          <Input
                            ref={sourceInputRef}
                            value={newRecord.source}
                            onChange={e =>
                              setNewRecord({ ...newRecord, source: e.target.value })
                            }
                            placeholder="Source"
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={newRecord.type}
                            onChange={e =>
                              setNewRecord({ ...newRecord, type: e.target.value })
                            }
                            placeholder="Type"
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={newRecord.quantity}
                            onChange={e =>
                              setNewRecord({ ...newRecord, quantity: e.target.value })
                            }
                            placeholder="Quantity"
                            className="h-8"
                            min="0"
                            step="1"
                          />
                        </td>
                        <td className="p-2">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">£</span>
                            <Input
                              type="number"
                              value={newRecord.cost}
                              onChange={e =>
                                setNewRecord({ ...newRecord, cost: e.target.value })
                              }
                              placeholder="0.00"
                              className="h-8 pl-6"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </td>
                        <td className="p-2">
                          <Input
                            value={newRecord.category}
                            onChange={e =>
                              setNewRecord({ ...newRecord, category: e.target.value })
                            }
                            placeholder="Category"
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <AsyncCombobox
                            value={newRecord.employee_id}
                            onValueChange={(value) =>
                              setNewRecord({ ...newRecord, employee_id: value })
                            }
                            onSearch={searchEmployees}
                            placeholder="Select employee..."
                            emptyText="No team members found"
                            className="h-8"
                          />
                        </td>
                        <td className="p-2 relative">
                          <Input
                            type="date"
                            value={newRecord.date_issued}
                            onChange={e =>
                              setNewRecord({ ...newRecord, date_issued: e.target.value })
                            }
                            className="h-8"
                          />
                        </td>
                        <td className="p-2 relative">
                          <Input
                            type="date"
                            value={newRecord.date_returned}
                            onChange={e =>
                              setNewRecord({ ...newRecord, date_returned: e.target.value })
                            }
                            className="h-8"
                          />
                        </td>
                        <td className="p-2 text-gray-400 text-xs">
                          {new Date().toLocaleDateString('en-GB')}
                        </td>
                        <td className="p-2 text-gray-400 text-xs">
                          {userProfile?.first_name} {userProfile?.last_name}
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleSaveNew}
                              className="h-7 w-7 p-0"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelNew}
                              className="h-7 w-7 p-0"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    )}

                    {/* Existing records */}
                    {ppeRecords.map((record, index) => {
                      const isEditing = editingRecordId === record.id;
                      const recordedByName = record.user_profiles
                        ? `${record.user_profiles.first_name} ${record.user_profiles.last_name}`
                        : 'Unknown';

                      return (
                        <motion.tr
                          key={record.id}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className={`border-b hover:bg-gray-50 transition-colors ${
                            isEditing ? 'bg-blue-50' : ''
                          }`}
                          onMouseEnter={() => setHoveredRow(record.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          {isEditing ? (
                            <>
                              <td className="p-2">
                                <Input
                                  value={editingData.source}
                                  onChange={e =>
                                    setEditingData({ ...editingData, source: e.target.value })
                                  }
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingData.type}
                                  onChange={e =>
                                    setEditingData({ ...editingData, type: e.target.value })
                                  }
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  value={editingData.quantity}
                                  onChange={e =>
                                    setEditingData({ ...editingData, quantity: e.target.value })
                                  }
                                  className="h-8"
                                  min="0"
                                  step="1"
                                />
                              </td>
                              <td className="p-2">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">£</span>
                                  <Input
                                    type="number"
                                    value={editingData.cost}
                                    onChange={e =>
                                      setEditingData({ ...editingData, cost: e.target.value })
                                    }
                                    placeholder="0.00"
                                    className="h-8 pl-6"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingData.category}
                                  onChange={e =>
                                    setEditingData({ ...editingData, category: e.target.value })
                                  }
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <AsyncCombobox
                                  value={editingData.employee_id}
                                  onValueChange={(value) =>
                                    setEditingData({ ...editingData, employee_id: value })
                                  }
                                  onSearch={searchEmployees}
                                  placeholder="Select employee..."
                                  emptyText="No team members found"
                                  className="h-8"
                                  displayLabel={
                                    record.employee
                                      ? `${record.employee.first_name} ${record.employee.last_name}`
                                      : undefined
                                  }
                                />
                              </td>
                              <td className="p-2 relative">
                                <Input
                                  type="date"
                                  value={editingData.date_issued}
                                  onChange={e =>
                                    setEditingData({ ...editingData, date_issued: e.target.value })
                                  }
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2 relative">
                                <Input
                                  type="date"
                                  value={editingData.date_returned}
                                  onChange={e =>
                                    setEditingData({ ...editingData, date_returned: e.target.value })
                                  }
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2 text-gray-600 text-xs">
                                {formatDate(record.uploaded_date)}
                              </td>
                              <td className="p-2 text-gray-600 text-xs">
                                {recordedByName}
                              </td>
                              <td className="p-2">
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSaveEdit(record.id)}
                                    className="h-7 w-7 p-0"
                                  >
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelEdit}
                                    className="h-7 w-7 p-0"
                                  >
                                    <X className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900 truncate"
                              >
                                {record.source || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900 truncate"
                              >
                                {record.type || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900"
                              >
                                {record.quantity || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900"
                              >
                                {record.cost ? `£${parseFloat(record.cost).toFixed(2)}` : '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900 truncate"
                              >
                                {record.category || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-900 truncate"
                              >
                                {record.employee ? `${record.employee.first_name} ${record.employee.last_name}` : '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-600 text-xs"
                              >
                                {formatDate(record.date_issued) || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-600 text-xs"
                              >
                                {formatDate(record.date_returned) || '-'}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-600 text-xs"
                              >
                                {formatDate(record.uploaded_date)}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2 text-gray-600 text-xs truncate"
                              >
                                {recordedByName}
                              </motion.td>
                              <motion.td
                                variants={cellVariants}
                                className="p-2"
                              >
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEdit(record)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDelete(record.id)}
                                      className="text-red-600"
                                    >
                                      <Trash className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </motion.td>
                            </>
                          )}
                        </motion.tr>
                      );
                    })}

                    {!loading && ppeRecords.length === 0 && !isAddingNew && (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-gray-500">
                          No PPE records yet. Click "Add Record" to get started.
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell3>
  );
}
